if (!customElements.get("support-scene")) {
  customElements.define(
    "support-scene",
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        this.transition?.cancel();
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.reduced = matchMedia("(prefers-reduced-motion: reduce)");
        const stage = this.querySelector("[data-scene-stage]");
        const art = this.querySelector("[data-scene-art]");
        const motion = this.querySelector("[data-scene-motion]");
        const choices = [...this.querySelectorAll("[data-scene-choice]")];
        if (!stage || !art || !motion) return;
        let motionOverride = false;
        let selection = 0;
        const loadImage = async (figure) => {
          const image = figure?.querySelector("img");
          if (!image) return;
          image.loading = "eager";
          if (image.decode) await image.decode();
        };
        const preload = (choice) => {
          const figure = [...this.querySelectorAll("[data-scene-view]")].find((view) => view.dataset.sceneView === choice.dataset.color);
          loadImage(figure).catch(() => {});
        };
        choices.forEach((choice) => {
          choice.addEventListener("pointerenter", () => preload(choice), { signal });
          choice.addEventListener("focus", () => preload(choice), { signal });
        });
        loadImage(this.querySelector("[data-scene-view]:not([hidden])"))
          .then(() => { if (!signal.aborted) choices.forEach(preload); })
          .catch(() => {});
        const updateMotion = () => {
          const enabled =
            !this.hasAttribute("data-motion-paused") &&
            (!this.reduced.matches || motionOverride);
          this.toggleAttribute("data-motion-enabled", enabled);
          motion.textContent = enabled ? "Pause motion" : "Resume motion";
          motion.setAttribute("aria-pressed", String(!enabled));
        };
        updateMotion();
        this.setAttribute("data-enhanced", "");
        this.setAttribute(
          "data-color",
          choices.find((a) => a.getAttribute("aria-current") === "true")
            ?.dataset.color || "white",
        );
        this.addEventListener(
          "click",
          async (event) => {
            const choice = event.target.closest("[data-scene-choice]");
            if (
              !choice ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            )
              return;
            event.preventDefault();
            const currentSelection = ++selection;
            this.transition?.cancel();
            choices.forEach((a) =>
              a.setAttribute("aria-current", String(a === choice)),
            );
            this.dataset.color = choice.dataset.color;
            const buy = this.querySelector("[data-scene-buy]");
            buy.href = choice.href;
            this.querySelector("[data-scene-price]").textContent =
              choice.dataset.price;
            const reference = this.querySelector("[data-scene-reference]");
            reference.hidden = !choice.dataset.reference;
            this.querySelector("[data-scene-reference-value]").textContent =
              choice.dataset.reference || "";
            this.querySelector("[data-scene-status]").textContent =
              `${choice.dataset.label}. ${choice.dataset.price} for 1 pair. ${choice.dataset.available === "false" ? "Currently unavailable." : "Choose your size on the product page."}`;
            const views = [...this.querySelectorAll("[data-scene-view]")];
            const view = views.find((figure) => figure.dataset.sceneView === choice.dataset.color);
            try {
              await loadImage(view);
            } catch {
              if (currentSelection === selection && !signal.aborted) {
                this.querySelector("[data-scene-status]").textContent = `Preview unavailable. Use the shop button to see ${choice.dataset.label} on the product page.`;
              }
              return;
            }
            if (currentSelection !== selection || signal.aborted) return;
            views.forEach((figure) => { figure.hidden = figure !== view; });
            if (view && this.hasAttribute("data-motion-enabled")) {
              this.transition = view.animate(
                [{ opacity: 0.55, transform: "translateY(4px) rotateY(-2deg)" }, { opacity: 1, transform: "none" }],
                { duration: 200, easing: "cubic-bezier(.2,0,0,1)" },
              );
            }
          },
          { signal },
        );
        const reset = () => {
          cancelAnimationFrame(this.frame);
          this.frame = 0;
          art.style.removeProperty("--tilt-x");
          art.style.removeProperty("--tilt-y");
        };
        stage.addEventListener(
          "pointermove",
          (event) => {
            if (
              event.pointerType !== "mouse" ||
              !this.hasAttribute("data-motion-enabled")
            )
              return;
            const r = stage.getBoundingClientRect();
            this.pointer = {
              x:
                Math.max(
                  -0.5,
                  Math.min(0.5, (event.clientX - r.left) / r.width - 0.5),
                ) * 8,
              y:
                Math.max(
                  -0.5,
                  Math.min(0.5, (event.clientY - r.top) / r.height - 0.5),
                ) * -6,
            };
            if (this.frame) return;
            this.frame = requestAnimationFrame(() => {
              this.frame = 0;
              art.style.setProperty("--tilt-x", `${this.pointer.y}deg`);
              art.style.setProperty("--tilt-y", `${this.pointer.x}deg`);
            });
          },
          { signal },
        );
        stage.addEventListener("pointerleave", reset, { signal });
        stage.addEventListener("pointercancel", reset, { signal });
        motion.addEventListener(
          "click",
          () => {
            const paused = this.hasAttribute("data-motion-enabled");
            this.toggleAttribute("data-motion-paused", paused);
            motionOverride = !paused && this.reduced.matches;
            this.transition?.cancel();
            updateMotion();
            reset();
          },
          { signal },
        );
        this.reduced.addEventListener(
          "change",
          () => {
            this.transition?.cancel();
            motionOverride = false;
            updateMotion();
            reset();
          },
          { signal },
        );
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.toggleAttribute(
              "data-visible",
              entry.isIntersecting && !document.hidden,
            );
            if (!entry.isIntersecting) reset();
          },
          { threshold: 0.1 },
        );
        this.observer.observe(stage);
        document.addEventListener(
          "visibilitychange",
          () => {
            if (document.hidden) {
              this.removeAttribute("data-visible");
              this.transition?.cancel();
              reset();
            } else {
              const r = stage.getBoundingClientRect();
              this.toggleAttribute(
                "data-visible",
                r.bottom > 0 && r.top < innerHeight,
              );
            }
          },
          { signal },
        );
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        this.transition?.cancel();
        cancelAnimationFrame(this.frame);
        this.frame = 0;
      }
    },
  );
}
